// The Sagrada Família.
import * as THREE from 'three';
import { PI } from '../lib/math.js';
import { pick, rnd, rr } from '../lib/random.js';
import { scene } from '../core/scene.js';
import { M, P, add, canvasTex } from '../lib/factory.js';
import { archGeo, octShape, prism, slabGeo, spire } from '../lib/shapes.js';
import {
  crossMat,
  fruitMats,
  mGold,
  mRed,
  mWhite,
  roofStone,
  stoneMid,
  stoneNew,
  stoneOld,
  towerNew,
  towerOld,
} from '../materials/stone.js';
import { pavingMat, stained } from '../materials/surfaces.js';
import { PCH, PLATE, colliders } from './eixample.js';

export let UP, V, V3, cranes;

export function buildSagrada() {
  // ---------- Sagrada Família ----------
  const sag = new THREE.Group();
  sag.position.y = 0.3;
  scene.add(sag);
  const A = (g, m, x, y, z) => add(g, m, x, y, z, sag);
  add(slabGeo(octShape(PLATE, PCH), 0.3, 0.06), pavingMat, 0, 0, 0);
  // an octagon like the block itself: a plain rectangle stuck out into the chamfered corners, where the lamp posts are,
  // and stopped the bike against nothing
  colliders.push({ type: 'oct', cx: 0, cz: 0, h: 12.1, ch: 6.2 });

  function pinnacle(s) {
    const g = new THREE.Group();
    add(new THREE.CylinderGeometry(0.16 * s, 0.26 * s, 0.9 * s, 8), mGold, 0, 0.45 * s, 0, g);
    add(new THREE.TorusGeometry(0.3 * s, 0.1 * s, 6, 10), mRed, 0, 0.95 * s, 0, g).rotation.x =
      PI / 2;
    const arm = new THREE.BoxGeometry(1.1 * s, 0.2 * s, 0.2 * s);
    add(arm, mWhite, 0, 1.25 * s, 0, g);
    add(arm, mWhite, 0, 1.25 * s, 0, g).rotation.y = PI / 2;
    for (let k = 0; k < 4; k++) {
      const a = (k * PI) / 2;
      add(
        new THREE.SphereGeometry(0.15 * s, 8, 6),
        k % 2 ? mRed : mGold,
        Math.cos(a) * 0.55 * s,
        1.25 * s,
        Math.sin(a) * 0.55 * s,
        g,
      );
    }
    add(new THREE.CylinderGeometry(0.08 * s, 0.12 * s, 0.6 * s, 6), mGold, 0, 1.55 * s, 0, g);
    add(new THREE.SphereGeometry(0.2 * s, 10, 8), mWhite, 0, 1.95 * s, 0, g);
    return g;
  }
  function bellTower(x, z, H, R0, mat) {
    const Hs = H * 0.82;
    A(spire(R0, Hs), mat, x, 0, z);
    const p = pinnacle(R0 * 1.25);
    p.position.set(x, Hs - 0.1, z);
    sag.add(p);
  }
  function stainedWin(x, y, z, w, h, ry) {
    const m = M(0x2a2f3a, {
      emissive: pick([0xff7043, 0x42a5f5, 0x66bb6a, 0xffca28, 0xab47bc]),
      emissiveIntensity: 0,
      side: THREE.DoubleSide,
      flatShading: false,
    });
    stained.push(m);
    const me = A(archGeo(w, h), m, x, y, z);
    me.rotation.y = ry;
    me.castShadow = false;
  }

  // nave, aisles, transept
  // the whole basilica fits inside its block: nave from z -8 (apse) to 9.6 (Glory facade)
  A(new THREE.BoxGeometry(8, 11, 17.6), stoneMid, 0, 5.5, 0.8);
  for (const sx of [-1, 1]) A(new THREE.BoxGeometry(2.2, 7.5, 17.6), stoneMid, sx * 5.1, 3.75, 0.8);
  A(prism(8.4, 4.5, 17.6), roofStone, 0, 11, 0.8);
  A(new THREE.BoxGeometry(15, 11, 7.5), stoneMid, 0, 5.5, 0);
  A(prism(7.9, 4.5, 15), roofStone, 0, 11, 0).rotation.y = PI / 2;
  const bays = [-6.8, -5, 5, 6.8, 8.6];
  for (const z of bays)
    for (const sx of [-1, 1]) {
      stainedWin(sx * 6.23, 1.6, z, 1, 3.4, (sx * PI) / 2);
      stainedWin(sx * 4.03, 8, z, 0.8, 2.6, (sx * PI) / 2);
      A(new THREE.ConeGeometry(0.38, 2, 6), stoneMid, sx * 5.9, 8.5, z);
      for (let k = 0; k < 3; k++)
        A(
          new THREE.SphereGeometry(0.26, 8, 6),
          pick(fruitMats),
          sx * 5.9 + rr(-0.2, 0.2),
          9.6 + rr(0, 0.3),
          z + rr(-0.25, 0.25),
        );
      A(new THREE.ConeGeometry(0.3, 1.6, 6), stoneMid, sx * 4, 11.8, z);
    }

  // apse with its ring of slender spires
  A(new THREE.CylinderGeometry(3.9, 3.9, 10.5, 16, 1, false, PI / 2, PI), stoneMid, 0, 5.25, -8);
  A(new THREE.ConeGeometry(3.9, 3.5, 16, 1, false, PI / 2, PI), roofStone, 0, 12.25, -8);
  for (let k = 0; k < 7; k++) {
    const t = PI / 2 + (k * PI) / 6,
      x = 4.4 * Math.sin(t),
      z = -8 + 4.4 * Math.cos(t);
    A(spire(0.55, 11.5, 0.85, 8), towerNew, x, 0, z);
    A(new THREE.SphereGeometry(0.32, 8, 6), pick(fruitMats), x, 11.7, z);
  }

  // Nativity (east, old dark stone) and Passion (west, new pale stone) facades
  const tz = [-4.8, -1.6, 1.6, 4.8],
    hN = [24.5, 26.8, 26.8, 24.5],
    hP = [26, 28.2, 28.2, 26];
  tz.forEach((z, i) => {
    bellTower(8.9, z, hN[i], 1.12, towerOld);
    bellTower(-8.9, z, hP[i], 1.12, towerNew);
  });
  for (const [sx, mat] of [
    [1, stoneOld],
    [-1, stoneNew],
  ]) {
    A(new THREE.BoxGeometry(2.6, 10.5, 12.6), mat, sx * 8.9, 5.25, 0);
    A(prism(12.6, 5, 2.6), mat, sx * 8.9, 10.5, 0).rotation.y = PI / 2;
  }
  const doorMat = M(0x3b2f25);
  for (const [z, w, h] of [
    [-3.2, 1.9, 4.6],
    [0, 2.5, 6],
    [3.2, 1.9, 4.6],
  ]) {
    A(archGeo(w, h), doorMat, 10.22, 0, z).rotation.y = PI / 2;
    A(archGeo(w, h), doorMat, -10.22, 0, z).rotation.y = -PI / 2;
  }
  // Nativity: organic, dripping stone + the cypress with white doves
  for (let k = 0; k < 80; k++) {
    const y = rr(4.8, 14),
      lim = y > 10.5 ? 6.3 * (1 - (y - 10.5) / 5) : 6;
    A(
      new THREE.IcosahedronGeometry(rr(0.22, 0.55), 0),
      stoneOld,
      rr(10.05, 10.5),
      y,
      rr(-lim, lim),
    );
  }
  A(new THREE.ConeGeometry(1, 4, 8), M(0x3d7a4f), 10.6, 12.3, 0);
  for (let k = 0; k < 10; k++) {
    const a = rr(0, PI * 2),
      t = rr(0.1, 0.8),
      r = 1 - t + 0.12;
    A(
      new THREE.SphereGeometry(0.14, 6, 5),
      mWhite,
      10.6 + Math.cos(a) * r,
      10.3 + t * 4,
      Math.sin(a) * r,
    );
  }
  // Passion: bone-like leaning columns under a flat pediment
  for (let i = 0; i < 6; i++) {
    const c = A(new THREE.CylinderGeometry(0.28, 0.42, 7.6, 7), stoneNew, -11, 3.8, -5 + 2 * i);
    c.rotation.z = i % 2 ? 0.14 : -0.1;
    c.rotation.x = rr(-0.08, 0.08);
  }
  A(new THREE.BoxGeometry(2.8, 1, 13), stoneNew, -11.2, 7.9, 0);
  // Glory facade, finished: stone front with a porch of seven columns under a stepped pediment, bronze doors
  A(new THREE.BoxGeometry(11, 9, 2), stoneNew, 0, 4.5, 10.6);
  for (let k = 0; k < 7; k++)
    A(new THREE.CylinderGeometry(0.32, 0.4, 6.4, 10), stoneNew, -4.5 + k * 1.5, 3.2, 12.1);
  A(new THREE.BoxGeometry(11.4, 0.7, 2.2), stoneNew, 0, 6.75, 12);
  for (let k = 0; k < 4; k++)
    A(new THREE.BoxGeometry(9 - k * 2.2, 0.55, 1.6), stoneNew, 0, 7.35 + k * 0.55, 11.7);
  for (const x of [-3, 0, 3])
    A(
      new THREE.BoxGeometry(1.5, 3.2, 0.1),
      P(0x8a6a2e, { roughness: 0.35, metalness: 0.7 }),
      x,
      1.9,
      11.62,
    );

  // central towers: Jesus (cross), four Evangelists, Mary (star)
  A(new THREE.CylinderGeometry(2.7, 3, 5, 16), towerNew, 0, 11.5, 0);
  A(spire(2.3, 30, 0.82, 16), towerNew, 0, 9, 0);
  const cross = new THREE.Group();
  cross.position.set(0, 39.2, 0);
  sag.add(cross);
  add(new THREE.CylinderGeometry(0.35, 0.45, 1.2, 8), crossMat, 0, 0.6, 0, cross);
  add(new THREE.BoxGeometry(0.8, 3.4, 0.8), crossMat, 0, 2.9, 0, cross);
  add(new THREE.BoxGeometry(3, 0.8, 0.8), crossMat, 0, 3.2, 0, cross);
  add(new THREE.BoxGeometry(0.8, 0.8, 3), crossMat, 0, 3.2, 0, cross);
  UP = new THREE.Vector3(0, 1, 0);
  V = (x, y, z) => new THREE.Vector3(x, y, z);
  V3 = V;
  [
    [1, 1, mGold],
    [1, -1, mWhite],
    [-1, 1, mRed],
    [-1, -1, mGold],
  ].forEach(([sx, sz, em]) => {
    const x = sx * 3.7,
      z = sz * 3.7;
    A(spire(1.45, 21, 0.8, 12), towerNew, x, 9, z);
    const g = new THREE.Group();
    g.position.set(x, 30, z);
    sag.add(g);
    add(new THREE.CylinderGeometry(0.15, 0.25, 0.7, 6), mGold, 0, 0.35, 0, g);
    add(new THREE.SphereGeometry(0.55, 10, 8), em, 0, 1.1, 0, g);
    for (let k = 0; k < 8; k++) {
      const a = (k * PI) / 4,
        c = add(
          new THREE.ConeGeometry(0.12, 0.9, 5),
          mWhite,
          Math.cos(a) * 0.55,
          1.3,
          Math.sin(a) * 0.55,
          g,
        );
      c.quaternion.setFromUnitVectors(
        UP,
        new THREE.Vector3(Math.cos(a) * 0.6, 1, Math.sin(a) * 0.6).normalize(),
      );
    }
  });
  A(spire(1.65, 21.5, 0.8, 14), towerNew, 0, 9, -6.4);
  const star = new THREE.Group();
  star.position.set(0, 32, -6.4);
  sag.add(star);
  add(new THREE.CylinderGeometry(0.15, 0.3, 1.6, 6), mGold, 0, -0.9, 0, star);
  add(new THREE.IcosahedronGeometry(0.6, 0), crossMat, 0, 0, 0, star);
  const ico = new THREE.IcosahedronGeometry(1, 0).attributes.position,
    seen = new Set();
  for (let i = 0; i < ico.count; i++) {
    const v = new THREE.Vector3().fromBufferAttribute(ico, i),
      key = v
        .toArray()
        .map((n) => n.toFixed(3))
        .join();
    if (seen.has(key)) continue;
    seen.add(key);
    const c = add(
      new THREE.ConeGeometry(0.22, 1, 5),
      crossMat,
      v.x * 1.05,
      v.y * 1.05,
      v.z * 1.05,
      star,
    );
    c.quaternion.setFromUnitVectors(UP, v.normalize());
  }

  // carved stone texture and sculptural detail on the basilica
  {
    const stoneTex = canvasTex(128, 128, (c) => {
      c.fillStyle = '#fff';
      c.fillRect(0, 0, 128, 128);
      for (let k = 0; k < 2200; k++) {
        const v = 205 + Math.floor(rnd() * 50);
        c.fillStyle = `rgb(${v},${v - 4},${v - 10})`;
        c.fillRect(rnd() * 128, rnd() * 128, rr(1, 3), rr(1, 3));
      }
      c.fillStyle = 'rgba(90,70,50,.14)';
      for (let x = 0; x < 128; x += rr(6, 12)) c.fillRect(x, 0, 1.5, 128);
      c.fillStyle = 'rgba(90,70,50,.1)';
      for (let y = 0; y < 128; y += 16) c.fillRect(0, y, 128, 1);
    });
    for (const m of [stoneOld, stoneNew, stoneMid, roofStone]) {
      m.map = stoneTex;
      m.needsUpdate = true;
    }
    for (const z of bays)
      for (const sx of [-1, 1]) {
        const m = M(0x2a2f3a, {
          emissive: pick([0xff7043, 0x42a5f5, 0x66bb6a, 0xffca28, 0xab47bc]),
          emissiveIntensity: 0,
          side: THREE.DoubleSide,
          flatShading: false,
        });
        stained.push(m);
        A(new THREE.CircleGeometry(0.36, 24), m, sx * 4.04, 10.75, z).rotation.y = (sx * PI) / 2;
        A(new THREE.TorusGeometry(0.38, 0.05, 6, 24), stoneMid, sx * 4.05, 10.75, z).rotation.y =
          (sx * PI) / 2;
      }
    for (let k = 0; k < 26; k++) {
      const y = rr(5, 10),
        z = rr(-5.2, 5.2),
        f = new THREE.Group();
      f.position.set(10.45, y, z);
      f.rotation.y = PI / 2;
      sag.add(f);
      add(new THREE.CapsuleGeometry(0.085, 0.22, 4, 8), stoneOld, 0, 0, 0, f);
      add(new THREE.SphereGeometry(0.07, 10, 8), stoneOld, 0, 0.2, 0, f);
    }
    for (let k = 0; k < 8; k++) {
      const f = new THREE.Group();
      f.position.set(-11.85, rr(4.4, 6.4), -4.6 + k * 1.3);
      sag.add(f);
      add(new THREE.BoxGeometry(0.2, 0.55, 0.22).rotateZ(rr(-0.2, 0.2)), stoneNew, 0, 0, 0, f);
      add(new THREE.BoxGeometry(0.15, 0.17, 0.15), stoneNew, 0, 0.36, 0, f);
    }
  }
  // the basilica is finished: no tower cranes any more (the list stays for the code that animates them)
  cranes = [];
}
